import React from 'react';
import { Sparkles, Layers } from 'lucide-react';
import { DashboardPagesPanel } from './DashboardPagesPanel';
import { MarksCard } from './MarksCard';
import { VisualListCard } from './VisualListCard';
import { VisualToolbar } from './VisualToolbar';
import { AiTextConfig } from './VisualTypeConfigs';
import { KpiConfig } from './VisualTypeConfigs';
import { ImageConfig } from './VisualTypeConfigs';
import { RichTextConfig } from './VisualTypeConfigs';
import { ShapeConfig } from './VisualTypeConfigs';
import { ConfigPanel } from './ConfigPanel';
import { TableCalculations } from './TableCalculations';
import { DualAxisPicker } from './DualAxisPicker';
import { DataSetsPanel } from './DataSetsPanel';

const SIMPLE_VIZ_TYPES = ['ai_text', 'image', 'rich_text', 'shape'];

/**
 * Render whatever tab content the builder's middle column is currently on.
 * The caller owns all state — this is a pure "switch-on-middleTab" renderer.
 */
export default function MiddleTabContent({
  middleTab, designerMode, currentVisual, activeVisual, renderShelves, visuals, reportName,
  // Data tab
  pages, setPages, activePageId, setActivePageId, setPageVisuals,
  pageVisuals, gridLayouts, setGridLayouts, pageNavStyle, setPageNavStyle,
  reportPages,
  markColor, setMarkColor, markSize, setMarkSize, markLabel, setMarkLabel, markDetail, setMarkDetail,
  // Visuals tab
  addVisual, setVisuals, setActiveVisual, setTextBlocks, setAnnotations, getMaxZIndex,
  updateVisual, removeVisual, recommendations,
  visualResults, rowDims, valueMeasures, loadingAiVisuals, setAiContents,
  getVizConfig, setVizConfig, showGrid, setShowGrid, showLegend, setShowLegend,
  refLineValue, setRefLineValue, refLineLabel, setRefLineLabel, parameters, allFields,
  reportTableSpanPages, setReportTableSpanPages, reportTableRepeatHeader, setReportTableRepeatHeader,
  tableCalcs, setTableCalcs, dualAxisField, setDualAxisField, dataSets, setDataSets,
  themeActive,
}) {
  // Distinct COLOR/SERIES values (sorted by total) for the active visual —
  // powers the per-series color pickers in ConfigPanel's Series section.
  const seriesValues = React.useMemo(() => {
    const cd = currentVisual?.colDim;
    if (!cd) return [];
    const rows = visualResults?.[activeVisual]?.result?.data || [];
    if (!rows.length) return [];
    const norm = (s) => String(s).toLowerCase().replace(/[\s_]+/g, '');
    const target = norm(cd);
    const key = (cd in rows[0]) ? cd : Object.keys(rows[0]).find(k => norm(k) === target);
    if (!key) return [];
    const measures = currentVisual?.valueMeasures || [];
    const totals = {};
    rows.forEach(r => {
      const v = r[key];
      if (v === null || v === undefined) return;
      const label = String(v);
      const amt = measures.reduce((s, m) => s + (Number(r[m]) || 0), 0);
      totals[label] = (totals[label] || 0) + amt;
    });
    return Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
  }, [currentVisual, visualResults, activeVisual]);

  if (middleTab === 'pages') {
    return (
      <DashboardPagesPanel
        pages={pages} setPages={setPages}
        activePageId={activePageId} setActivePageId={setActivePageId}
        setPageVisuals={setPageVisuals}
        pageVisuals={pageVisuals} gridLayouts={gridLayouts} setGridLayouts={setGridLayouts}
        pageNavStyle={pageNavStyle} setPageNavStyle={setPageNavStyle}
      />
    );
  }

  if (middleTab === 'data') {
    const showShelves = !SIMPLE_VIZ_TYPES.includes(currentVisual?.vizType);
    return (
      <>
        {designerMode === 'report' && (
          <div className="p-3 border-b border-border/30">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Report Pages</p>
              <span className="text-[9px] text-primary font-medium">{reportPages.length} page{reportPages.length !== 1 ? 's' : ''}</span>
            </div>
            <p className="text-[8px] text-muted-foreground/80">Use the page navigator on the canvas to add, delete, or switch pages.</p>
          </div>
        )}
        {showShelves && (
          <>
            <div className="p-3 space-y-2 border-b border-border/30">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Drop Zones</p>
              {renderShelves()}
            </div>
            <MarksCard
              markColor={markColor} setMarkColor={setMarkColor}
              markSize={markSize} setMarkSize={setMarkSize}
              markLabel={markLabel} setMarkLabel={setMarkLabel}
              markDetail={markDetail} setMarkDetail={setMarkDetail}
            />
          </>
        )}
        {!showShelves && (
          <div className="p-3 border-b border-border/30">
            <p className="text-xs text-muted-foreground/70 italic text-center py-4">Configure this visual in the Visuals tab.</p>
          </div>
        )}
      </>
    );
  }

  if (middleTab === 'visuals') {
    return (
      <>
        <div className="p-3 border-b border-border/30">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Visuals</p>
            <VisualToolbar
              addVisual={addVisual}
              setVisuals={setVisuals} setActiveVisual={setActiveVisual}
              setTextBlocks={setTextBlocks} setAnnotations={setAnnotations}
              getMaxZIndex={getMaxZIndex}
            />
          </div>
          {visuals.map(v => (
            <VisualListCard
              key={v.id}
              v={v}
              isActive={activeVisual === v.id}
              designerMode={designerMode}
              canRemove={visuals.length > 1}
              onSetActive={() => setActiveVisual(v.id)}
              onUpdate={u => updateVisual(v.id, u)}
              onRemove={() => removeVisual(v.id)}
            />
          ))}
        </div>
        {recommendations.length > 0 && (
          <div className="p-3 border-b border-border/30">
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5" /> Suggested
            </p>
            {recommendations.slice(0, 3).map((r) => (
              <button
                key={`rec-${r.type}`}
                onClick={() => updateVisual(activeVisual, { vizType: r.type })}
                className="w-full text-left px-2 py-0.5 rounded text-[9px] hover:bg-muted/30 text-muted-foreground"
              >
                {r.type.replace('_', ' ')} ({Math.round(r.confidence * 100)}%)
              </button>
            ))}
          </div>
        )}
        <AiTextConfig currentVisual={currentVisual} activeVisual={activeVisual} updateVisual={updateVisual} visuals={visuals} visualResults={visualResults} rowDims={rowDims} valueMeasures={valueMeasures} reportName={reportName} loadingAiVisuals={loadingAiVisuals} setAiContents={setAiContents} />
        <KpiConfig currentVisual={currentVisual} activeVisual={activeVisual} updateVisual={updateVisual} />
        <ImageConfig currentVisual={currentVisual} activeVisual={activeVisual} updateVisual={updateVisual} />
        <RichTextConfig currentVisual={currentVisual} activeVisual={activeVisual} updateVisual={updateVisual} />
        <ShapeConfig currentVisual={currentVisual} activeVisual={activeVisual} updateVisual={updateVisual} />
        <ConfigPanel
          vizType={currentVisual ? currentVisual.vizType : 'table'}
          vizConfig={getVizConfig(activeVisual)}
          onConfigChange={(updates) => setVizConfig(activeVisual, updates)}
          showGrid={showGrid} setShowGrid={setShowGrid}
          showLegend={showLegend} setShowLegend={setShowLegend}
          refLineValue={refLineValue} setRefLineValue={setRefLineValue}
          refLineLabel={refLineLabel} setRefLineLabel={setRefLineLabel}
          parameters={parameters} allFields={allFields}
          themeActive={themeActive}
          currentVisual={currentVisual}
          onUpdateVisual={(u) => updateVisual(activeVisual, u)}
          designerMode={designerMode}
          reportTableSpanPages={reportTableSpanPages} setReportTableSpanPages={setReportTableSpanPages}
          reportTableRepeatHeader={reportTableRepeatHeader} setReportTableRepeatHeader={setReportTableRepeatHeader}
          seriesValues={seriesValues}
        />
        <TableCalculations valueMeasures={valueMeasures} tableCalcs={tableCalcs} setTableCalcs={setTableCalcs} allFields={allFields} />
        <DualAxisPicker
          vizType={currentVisual?.vizType}
          valueMeasures={valueMeasures}
          dualAxisField={dualAxisField}
          setDualAxisField={setDualAxisField}
        />
        <DataSetsPanel dataSets={dataSets} setDataSets={setDataSets} rowDims={rowDims} />
      </>
    );
  }

  return null;
}
